import { ANALYTICS_EVENTS } from '@server/analytics-events';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { GatewaySpinner } from '@/_shared/components/GatewayShell';
import { RudderstackBrandLogo } from '@/_shared/components/RudderstackBrandLogo';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { trackClientEvent } from '@/_shared/lib/analytics';
import { orpc } from '@/_shared/lib/orpc';

import { DashboardPanel } from '../components/DashboardPanel';
import { DashboardParamBar } from '../components/DashboardParamBar';
import { PublicUnavailable } from '../components/PublicUnavailable';
import { useDeferredParamDefaults } from '../hooks/useDeferredParamDefaults';
import { useDynamicParamOptions } from '../hooks/useDynamicParamOptions';
import { computeDefaults, searchParamsToValues, valuesToSearchParams } from '../lib/dashboard-params';
import { panelError } from '../lib/panel-error';
import type { DashboardPanel as DashboardPanelType } from '../types';

export function PublicDashboard(): React.ReactElement {
  const { token } = useParams<{ token: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // Fetch via share token. The backend returns { disabled: true } for
  // unknown/private/workspace-disabled cases — we collapse all of those to
  // the PublicUnavailable page so the failure mode does not leak which
  // condition fired.
  const {
    data: dashboardData,
    isLoading,
    error,
  } = useQuery(
    orpc.dashboard.getPublic.queryOptions({
      input: token ? { token } : { token: '' },
    }),
  );
  const dashboard = dashboardData && !dashboardData.disabled ? dashboardData.dashboard : null;
  usePageTitle(dashboard?.title);

  useEffect(() => {
    if (token && dashboard) {
      trackClientEvent(ANALYTICS_EVENTS.publicDashboardViewed, {
        dashboardId: dashboard.id,
        slug: dashboard.slug,
      });
    }
    // Deliberately depend on primitives, not the `dashboard` object reference: a background refetch
    // that only changes an unrelated field (title, description, panels) would otherwise re-fire this
    // effect and double-count a "publicDashboardViewed" analytics event for the same view. Same
    // pattern as AnalyticsIdentity.tsx.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see comment above: primitives are intentional, not a missing `dashboard` dep
  }, [token, dashboard?.id, dashboard?.slug]);

  // Resolve dynamic parameter options.
  // anonymous backend gate (token must resolve to this dashboard) accepts the
  // request — without it the call collapses to NOT_FOUND.
  const {
    parameters: resolvedParams,
    isLoading: paramsLoading,
    loadingParamNames,
  } = useDynamicParamOptions(dashboard?.id, dashboard?.spec.parameters ?? [], orpc, token);

  const paramValues = useMemo((): Record<string, string | string[]> => {
    if (!dashboard) return {};
    const defaults = computeDefaults(resolvedParams);
    const fromUrl = searchParamsToValues(searchParams, resolvedParams);
    return { ...defaults, ...fromUrl };
  }, [dashboard, resolvedParams, searchParams]);

  // Deferred defaults: seed missing params AND auto-correct invalid option values once loaded (AI-1186).
  useDeferredParamDefaults(!paramsLoading && !!dashboard, resolvedParams, searchParams, setSearchParams);

  const handleParamChange = useCallback(
    (newValues: Record<string, string | string[]>) => {
      setSearchParams(valuesToSearchParams(newValues), { replace: true });
    },
    [setSearchParams],
  );

  const sortedPanels = useMemo(() => {
    if (!dashboard) return [];
    return [...dashboard.spec.panels].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [dashboard]);

  if (isLoading) {
    return <GatewaySpinner mode="fullScreen" />;
  }

  // The backend collapses unknown / private / workspace-disabled tokens into
  // a single `{ disabled: true }` shape. We collapse error and disabled cases
  // into the same PublicUnavailable page so the failure mode does not leak
  // which condition fired.
  if (error || !dashboard) {
    return <PublicUnavailable />;
  }

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <PublicDashboardHeader title={dashboard.title} description={dashboard.description} />
      <DashboardParamBar
        parameters={resolvedParams}
        values={paramValues}
        onChange={handleParamChange}
        loadingParamNames={loadingParamNames}
        isPublic
      />
      <div className="flex-1">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-4 px-6 py-6 lg:grid-cols-2">
          {sortedPanels.map((panel) => (
            <PublicPanelWithData
              key={panel.id}
              dashboardId={dashboard.id}
              shareToken={token ?? ''}
              panel={panel}
              params={paramValues}
              paramsLoading={paramsLoading}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PublicDashboardHeader({
  title,
  description,
}: {
  title: string;
  description: string | null;
}): React.ReactElement {
  return (
    <div className="flex flex-col gap-2 border-b border-border px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <h1 className="truncate text-lg font-semibold text-foreground">{title}</h1>
        {description && <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{description}</p>}
      </div>
      <div className="mt-2 flex shrink-0 items-center justify-end gap-1.5 text-xs text-muted-foreground sm:mt-0 sm:ml-4">
        Powered by
        <RudderstackBrandLogo variant="full" className="h-4" />
      </div>
    </div>
  );
}

function PublicPanelWithData({
  dashboardId,
  shareToken,
  panel,
  params,
  paramsLoading,
}: {
  dashboardId: string;
  shareToken: string;
  panel: DashboardPanelType;
  params: Record<string, string | string[]>;
  paramsLoading?: boolean;
}): React.ReactElement {
  const queryOpts = orpc.dashboard.query.queryOptions({
    input: {
      dashboardId,
      panelId: panel.id,
      params,
      shareToken,
    },
  });
  const { data, isPending, error } = useQuery({
    ...queryOpts,
    enabled: !paramsLoading,
  });
  const failure = panelError(error);

  return (
    <div className={panel.width === 'full' ? 'lg:col-span-2' : 'lg:col-span-1'}>
      <DashboardPanel
        panel={panel}
        data={data ?? null}
        loading={isPending}
        // No refetch skeleton on a public dashboard: keep the prior rows during a same-key background
        // refetch (window refocus) instead of flashing a skeleton to external viewers.
        // `/pub/:token` calls the global `/rpc/dashboard/query`, where no workspace is in context, so
        // the server never sends a problem here: every viewer gets only "Query failed" (ACT2-1166).
        error={failure?.text ?? null}
        problem={failure?.problem ?? null}
      />
    </div>
  );
}
